import React from "react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";

const Section = ({ id, title, children }) => (
  <section id={id} className="border-b border-slate-200 py-12">
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600 mb-3">§ {id}</div>
        <h2 className="font-display font-bold text-3xl tracking-tighter text-slate-900">{title}</h2>
      </div>
      <div className="lg:col-span-8 text-slate-700 space-y-4 leading-relaxed text-[15px]">{children}</div>
    </div>
  </section>
);

export default function Terms() {
  return (
    <div className="bg-cosmos text-white" data-testid="terms-page">
      <MarketingNav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">Documento legal</div>
        <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tighter text-slate-900 mt-2">
          Términos &amp; Cumplimiento
        </h1>
        <p className="text-slate-600 mt-4 max-w-3xl text-lg">
          OpenPayments S.A. (en adelante, "OpenPayments") es una entidad de pago electrónica que opera bajo el marco
          regulatorio del Reino Unido y la Unión Europea. Esta es una demostración educativa; los servicios no constituyen
          asesoramiento financiero ni gestión real de fondos.
        </p>
        <div className="mt-10 text-xs font-mono-fin text-slate-500">Versión 2026.01 · Última actualización: enero 2026</div>

        <Section id="fca" title="Autorización FCA (Reino Unido)">
          <p>
            OpenPayments figura como entidad de dinero electrónico autorizada por la <b>Financial Conduct Authority</b> bajo el
            número de referencia <span className="font-mono-fin">9XX834</span> (demostrativo). Operamos conforme a la
            <i> Electronic Money Regulations 2011</i> y la <i>Payment Services Regulations 2017</i>.
          </p>
          <p>
            Los fondos de los clientes se segregan en cuentas de salvaguarda con entidades de crédito autorizadas,
            cumpliendo con las disposiciones de protección de fondos. Cualquier reclamación puede dirigirse al
            <i> Financial Ombudsman Service</i>.
          </p>
        </Section>

        <Section id="mica" title="Cumplimiento MiCA (UE 2023/1114)">
          <p>
            Conforme al <b>Reglamento (UE) 2023/1114 (MiCA)</b> sobre los mercados de criptoactivos, OpenPayments cumple
            con las obligaciones de transparencia, supervisión y protección al consumidor para servicios relacionados con
            criptoactivos.
          </p>
          <p>
            Aunque la arquitectura DeFi aquí descrita es conceptual, todos los sellos criptográficos (SHA-256) aplicados a
            certificados y comprobantes se generan respetando los principios de integridad, no repudio y trazabilidad.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Información precontractual completa al usuario.</li>
            <li>Política de gestión de conflictos de interés publicada.</li>
            <li>Procedimientos de prevención de abuso de mercado.</li>
            <li>Reservas de activos y derechos de redención claros.</li>
          </ul>
        </Section>

        <Section id="kyc" title="KYC, AML y prevención del fraude">
          <p>
            Aplicamos un proceso de <b>Conoce a tu Cliente (KYC)</b> previo a la activación plena de la cuenta, conforme
            a la 5ª y 6ª Directiva Antiblanqueo (AMLD5 / AMLD6) y la normativa local de cada país.
          </p>
          <p>
            La autenticación reforzada (SCA) se aplica bajo PSD2 a operaciones sensibles. Conservamos los registros de
            identidad y transacciones por el período legalmente exigido (mínimo 5 años).
          </p>
        </Section>

        <Section id="sepa" title="Servicios SEPA y transferencias">
          <p>
            Ofrecemos <b>SEPA Inmediata (SCT Inst)</b> con liquidación 24/7/365 en menos de 10 segundos, y SEPA Estándar
            con liquidación en T+1. También permitimos envíos a cuentas locales (CBU u otros números de cuenta) con
            verificación previa.
          </p>
          <p>
            Los límites diarios y mensuales por defecto pueden ajustarse según el nivel de verificación KYC del usuario.
            Las comisiones aplicables se publican en el panel del usuario antes de confirmar cualquier operación.
          </p>
        </Section>

        <Section id="documentos" title="Certificados y comprobantes">
          <p>
            Los certificados de cuenta y comprobantes de movimiento se generan automáticamente en formato PDF.
            Cada documento incluye un <b>hash criptográfico SHA-256</b> que actúa como firma digital y permite verificar
            su integridad.
          </p>
          <p>
            En caso de discrepancia entre un documento descargado y los registros internos de OpenPayments, prevalecerán
            estos últimos. La verificación blockchain conceptual no constituye garantía de liquidez real.
          </p>
        </Section>

        <Section id="privacidad" title="Privacidad y datos (GDPR)">
          <p>
            Tratamos los datos personales de conformidad con el <b>Reglamento (UE) 2016/679 (GDPR)</b>. El usuario tiene
            derecho a acceder, rectificar, suprimir, oponerse, limitar el tratamiento, portabilidad y a no ser objeto de
            decisiones automatizadas.
          </p>
          <p>
            Los datos se almacenan en infraestructuras dentro del Espacio Económico Europeo y se cifran en tránsito y en
            reposo. Para ejercer cualquier derecho: <span className="font-mono-fin">privacy@openpayments.com</span>.
          </p>
        </Section>

        <Section id="responsabilidad" title="Limitación de responsabilidad">
          <p>
            OpenPayments no se hace responsable de pérdidas indirectas, lucro cesante ni de errores derivados del uso
            indebido de la plataforma. Este servicio es de naturaleza demostrativa y no debe utilizarse para gestionar
            fondos reales.
          </p>
        </Section>

        <Section id="ley" title="Ley aplicable y jurisdicción">
          <p>
            Estos términos se rigen por la legislación española y europea aplicable. Para cualquier controversia,
            las partes se someten a los tribunales competentes del domicilio del consumidor o, en su defecto, a los de Madrid.
          </p>
        </Section>
      </div>
      <Footer />
    </div>
  );
}
